'use client';

import { useMemo } from 'react';
import useSWR from 'swr';

import { transactionsApi } from '@/lib/api';
import type { TransactionsMetaResponse } from '@/lib/api';
import { calendarScopeQuery } from '@/lib/calendarScope';
import { resolveCategoryColor } from '@/lib/transactions/categories';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/** Backend sentinel appended to `categoryBreakdown` for everything past the top segments. */
const OTHERS_SEGMENT_ID = '__others__';

const TOP_CATEGORIES_LIMIT = 5;

export interface TopCategoryRow {
  id: string;
  label: string;
  iconName: string | null;
  /** Accent from the shared hierarchical palette, so the hue matches /transactions. */
  color: string;
  amount: number;
  /** 0..1 share of TOTAL cycle spend — each bar reads as "% of everything spent". */
  ratio: number;
}

export interface TopCategoriesData {
  rows: TopCategoryRow[];
  /** Every expense in the cycle (not just the top 5) — the "Total no ciclo" footer. */
  total: number;
}

interface BreakdownSegment {
  categoryId: string;
  total: number;
}

function isRealSegment(segment: BreakdownSegment): boolean {
  return segment.categoryId !== OTHERS_SEGMENT_ID && segment.total > 0;
}

function byTotalDesc(a: BreakdownSegment, b: BreakdownSegment): number {
  return b.total - a.total;
}

/**
 * Derives the widget model from `/transactions/meta`. `categoryBreakdown` is
 * the right source (vs `categoryFacets`, which mixes income in): the backend
 * builds it expense-only, cash-flow-neutral rows excluded, grouped by parent
 * category and sorted desc with an `__others__` tail. Labels/icons resolve
 * from the response's own category metadata.
 */
export function selectTopCategories(meta: TransactionsMetaResponse): TopCategoriesData {
  const display = new Map<string, { label: string; iconName: string | null }>();
  function remember(id: string, label: string, iconName: string | null): void {
    if (id && label && !display.has(id)) display.set(id, { label, iconName });
  }
  for (const category of meta.categories ?? []) {
    remember(category.id, category.label, category.iconName);
    remember(category.parentCategoryId, category.parentLabel, category.parentIconName);
  }
  for (const facet of meta.categoryFacets ?? []) {
    remember(facet.id, facet.label, facet.iconName);
  }

  // Expense-oriented: each bar is the category's share of ALL cycle spend, so a
  // full bar would mean one category = 100% of spending (not "biggest of the top 5").
  const total = meta.categoryBreakdown?.total ?? 0;
  const segments = (meta.categoryBreakdown?.segments ?? [])
    .filter(isRealSegment)
    .sort(byTotalDesc)
    .slice(0, TOP_CATEGORIES_LIMIT);

  const rows = segments.map((segment): TopCategoryRow => {
    const info = display.get(segment.categoryId);
    return {
      id: segment.categoryId,
      label: info?.label ?? 'Outros',
      iconName: info?.iconName ?? 'circle-dashed',
      color: resolveCategoryColor(segment.categoryId),
      amount: segment.total,
      ratio: total > 0 ? segment.total / total : 0,
    };
  });

  return { rows, total };
}

/**
 * W7 · Top 5 categorias — the widget's own data: current-cycle (calendar
 * month, matching the header's "dia X de Y") spend by category from
 * `/transactions/meta` (the local `year`/`month`/`today` of `calendarScopeQuery`,
 * read at fetch time, resolve the month window server-side).
 * The key deliberately rides the `transactions-` prefix so the existing
 * mutation/sync revalidation sweeps (`revalidateTransactionCaches`,
 * FINANCIAL_DATA_KEY_PREFIXES) refresh this widget too.
 */
export function useHomeTopCategories(): {
  data: TopCategoriesData | undefined;
  error: unknown;
  isLoading: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const { data: meta, error, isLoading } = useSWR<TransactionsMetaResponse>(
    ['transactions-meta-home-top-categories', year, month],
    () => authedFetch((token) => transactionsApi.getMeta(token, calendarScopeQuery())),
    LONG_LIVED_SWR_OPTIONS,
  );

  const data = useMemo(() => (meta ? selectTopCategories(meta) : undefined), [meta]);

  return { data, error, isLoading };
}
